import { InfoIcon } from 'assets/icons';
import qrImage from 'assets/images/qrcode.png';
import { NotificationType } from 'common/enums';
import { Modal } from 'common/modal';
import { AppContext, appActions } from 'context';
import dayjs from 'dayjs';
import { classnames, getClipNameOrVaultTitle } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { useContext, useEffect, useState } from 'react';
import { AppLink, RouteName } from 'routes';
import styles from './qr-codes-details-modal.module.scss';

type QrCodesDetailsModalProps = {
  qrCodes: QrCode[];
  onClose: () => void;
  vault?: VaultType;
};

const QrCodesDetailsModal: React.FC<QrCodesDetailsModalProps> = ({
  qrCodes,
  onClose,
  vault,
}) => {
  const { dispatch, state } = useContext(AppContext);
  const [vaultQrCodes, setVaultQrCodes] = useState<VaultType>();

  const handleGetVault = async (vaultId: number) => {
    try {
      const vaultInfo = await VaultsController.getVault(vaultId);
      setVaultQrCodes(vaultInfo);
    } catch (error) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      onClose();
    }
  };

  useEffect(() => {
    if (!vault) {
      handleGetVault(qrCodes[0].vaultId);
    } else {
      setVaultQrCodes(vault);
    }
  }, []);

  const qrCodesListDetails = (codes: QrCode[]) => (
    <div className={styles.qrCodesListModal}>
      <div className="text__body__regular__medium__gray800">
        This vault has been linked to the following QR codes:
      </div>
      {codes.map((code) => (
        <div key={code.id} className={styles.qrCodeItem}>
          <button
            onClick={() =>
              dispatch({
                type: appActions.SET_IMAGE_TO_ENLARGE,
                imageToEnlarge: code.image || qrImage,
                isQrCodeImageEnlarged: true,
              })
            }
            type="button"
          >
            <img
              className={styles.qrImage}
              src={code.image || qrImage}
              alt="QR code"
            />
          </button>
          <div>
            <div className="text__body__semi__bold__medium__gray800">
              {vaultQrCodes &&
                getClipNameOrVaultTitle(code.clipId, vaultQrCodes)}
            </div>
            <div className="text__body__regular__small__gray700">
              By {code.owner}
            </div>
            <div className="text__body__regular__small__gray700">
              {dayjs(code.linkDate).format('MMMM Do, YYYY')}
            </div>
          </div>
        </div>
      ))}
      <div className={styles.infoContainer}>
        <InfoIcon className={styles.infoIcon} />
        <div>
          <div className={styles.infoText}>
            <span className={classnames('text__body__regular__tiny__gray700')}>
              Buy QR codes from the{' '}
              <AppLink className={styles.infoLink} routeName={RouteName.Store}>
                <span className="text__body__regular__tiny__brand500">
                  Generational Story Store
                </span>
              </AppLink>
              .
            </span>
          </div>
          <div className={styles.infoText}>
            <span className={classnames('text__body__regular__tiny__gray700')}>
              Manage QR codes from the Generational Story App.
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      className={classnames({
        [styles.transparentBackground]: !!state.imageToEnlarge,
      })}
      contentClassName={styles.container}
      title="Active QR Code"
      onClose={onClose}
    >
      {qrCodesListDetails(qrCodes)}
    </Modal>
  );
};

export { QrCodesDetailsModal };
